import { alpha, createTheme } from '@mui/material/styles';
import lightTheme from './lightTheme';

// MIKIWAME Brand Colors (V2 Premium) - Dark Theme
const darkColors = {
  background: {
    default: '#0a192f', // Deep Midnight Blue (Solid backing)
    paper: 'rgba(17, 34, 64, 0.75)', // Glassmorphic logic
    subtle: 'rgba(30, 41, 59, 0.7)',
  },
  primary: {
    main: '#667eea', // Vibrant Blue/Purple
    light: '#7c90ed',
    dark: '#5a6fd8',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#764ba2', // Deep Purple
    light: '#a27dbf',
    dark: '#563582',
    contrastText: '#ffffff',
  },
  text: {
    primary: '#e6f1ff', // Bright White/Blueish
    secondary: '#8892b0', // Muted Blue/Grey
    disabled: '#495670',
  },
  action: {
    active: '#667eea',
    hover: 'rgba(102, 126, 234, 0.12)',
    selected: 'rgba(102, 126, 234, 0.24)',
  },
  success: {
    main: '#64ffda', // Neon Teal
  },
  warning: {
    main: '#ffd700', // Gold
  },
  error: {
    main: '#ff6b6b', // Soft Red
  },
  info: {
    main: '#66d9ef', // Neon Blue
  },
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: darkColors.primary,
    secondary: darkColors.secondary,
    background: {
      default: darkColors.background.default,
      paper: darkColors.background.paper,
    },
    text: darkColors.text,
    success: darkColors.success,
    warning: darkColors.warning,
    error: darkColors.error,
    info: darkColors.info,
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic UI", "Segoe UI", system-ui, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em', color: darkColors.text.primary },
    h2: { fontWeight: 800, letterSpacing: '-0.02em', color: darkColors.text.primary },
    h3: { fontWeight: 700, letterSpacing: '-0.01em', color: darkColors.text.primary },
    h4: { fontWeight: 700, letterSpacing: '-0.01em', color: darkColors.text.primary },
    h5: { fontWeight: 600, color: darkColors.text.primary },
    h6: { fontWeight: 600, color: darkColors.text.primary },
    body1: { lineHeight: 1.6, color: darkColors.text.primary },
    body2: { lineHeight: 1.6, color: darkColors.text.secondary },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a192f',
          backgroundImage: `
            radial-gradient(at 0% 0%, hsla(253,16%,7%,1) 0, transparent 50%), 
            radial-gradient(at 50% 0%, hsla(225,39%,30%,1) 0, transparent 50%), 
            radial-gradient(at 100% 0%, hsla(339,49%,30%,1) 0, transparent 50%)
          `, // Subtle background gradient depth
          backgroundAttachment: 'fixed',
          scrollbarColor: '#8892b0 #0a192f',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            width: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#233554',
            minHeight: 24,
            border: '2px solid #0a192f',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(17, 34, 64, 0.6)', // Semi-transparent
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            borderColor: darkColors.primary.main,
            backgroundColor: 'rgba(17, 34, 64, 0.8)',
            boxShadow: `0 12px 40px -8px ${alpha(darkColors.primary.main, 0.3)}`,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        contained: {
          background: `linear-gradient(45deg, ${darkColors.primary.main}, ${darkColors.secondary.main})`,
          boxShadow: `0 4px 14px 0 ${alpha(darkColors.primary.main, 0.4)}`,
          '&:hover': {
            boxShadow: `0 6px 20px 0 ${alpha(darkColors.primary.main, 0.6)}`,
            transform: 'scale(1.02)',
          },
        },
        outlined: {
          borderColor: darkColors.primary.main,
          borderWidth: '1px',
          '&:hover': {
            borderWidth: '1px',
            backgroundColor: alpha(darkColors.primary.main, 0.1),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          backdropFilter: 'blur(4px)',
        },
        filled: {
          border: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 25, 47, 0.7)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(10, 25, 47, 0.85)',
          backdropFilter: 'blur(16px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
});

// 後方互換性のためmikiwameThemeもエクスポート
export const mikiwameTheme = darkTheme;
export { darkTheme, lightTheme };
export default darkTheme;
